import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const FADE_DURATION_MS = 200;

export class GalleryManager implements Plugin {
  private currentIndex: number = 0;
  private images: string[] = [];
  private isTransitioning: boolean = false;
  private readonly stageImage: HTMLImageElement | null;
  private readonly counter: HTMLElement | null;
  private readonly thumbsContainer: HTMLElement | null;
  private readonly nextBtn: HTMLElement | null;
  private readonly prevBtn: HTMLElement | null;

  private readonly maxVisibleThumbs: number = 5;

  constructor(private readonly element: HTMLElement) {
    this.stageImage = element.querySelector(
      ".com-lightboxB2B__stage .com-imageB2B__image",
    );
    this.counter = element.querySelector(".com-lightboxB2B__counter");
    this.thumbsContainer = element.querySelector(".com-lightboxB2B__thumbs");
    this.prevBtn = element.querySelector(".core-carouselNav--left");
    this.nextBtn = element.querySelector(".core-carouselNav--right");

    const dataImages = this.element.dataset.images;

    if (dataImages) {
      try {
        this.images = JSON.parse(dataImages);
      } catch (error) {
        console.error(
          "GalleryManager: Erro ao ler data-images via JSON",
          error,
        );
        this.images = [];
      }
    } else {
      console.warn("GalleryManager: data-images não encontrado");
    }
  }

  init(): void {
    const thumbs = Array.from(
      this.element.querySelectorAll(".com-lightboxB2B__thumb .com-imageB2B"),
    );
    const selectedIndex = thumbs.findIndex((t) =>
      t.classList.contains("com-imageB2B--selected"),
    );

    this.currentIndex = selectedIndex !== -1 ? selectedIndex : 0;

    this.attachEventListeners();
    this.updateUI();
  }

  private attachEventListeners(): void {
    this.prevBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      if (!this.isTransitioning) this.navigate(-1);
    });
    this.nextBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      if (!this.isTransitioning) this.navigate(1);
    });

    const thumbContainers = this.element.querySelectorAll(
      ".com-lightboxB2B__thumb",
    );
    thumbContainers.forEach((container, index) => {
      container.addEventListener("click", () => {
        if (!this.isTransitioning && this.currentIndex !== index) {
          this.currentIndex = index;
          this.updateUI();
        }
      });
    });
  }

  private navigate(direction: number): void {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.images.length) {
      this.currentIndex = newIndex;
      this.updateUI();
    }
  }

  private updateUI(): void {
    const newSrc = this.images[this.currentIndex];
    const wrapper = this.stageImage?.closest(
      ".com-lightboxB2B__stage-wrapper",
    ) as HTMLElement | null;

    if (this.counter) {
      this.counter.textContent = `${this.currentIndex + 1}/${this.images.length}`;
    }

    const allThumbs = this.element.querySelectorAll(
      ".com-lightboxB2B__thumb .com-imageB2B",
    );
    allThumbs.forEach((thumb, index) => {
      thumb.classList.toggle(
        "com-imageB2B--selected",
        index === this.currentIndex,
      );
    });

    this.scrollThumbnails();

    if (!this.stageImage || !newSrc || !wrapper) return;

    const currentSrc = this.stageImage.getAttribute("src") ?? "";
    const isSameImage =
      currentSrc === newSrc || this.stageImage.src.endsWith(newSrc);
    if (isSameImage) return;

    if (this.isTransitioning) return;

    this.isTransitioning = true;
    wrapper.classList.add("is-fading-out");

    setTimeout(() => {
      if (this.stageImage && this.images[this.currentIndex]) {
        this.stageImage.src = this.images[this.currentIndex];
      }
      wrapper.classList.remove("is-fading-out");
      this.isTransitioning = false;
    }, FADE_DURATION_MS);
  }

  private scrollThumbnails(): void {
    if (!this.thumbsContainer) return;

    const thumbElements = this.thumbsContainer.children;
    if (thumbElements.length === 0) return;

    const firstThumb = thumbElements[0] as HTMLElement;

    const style = window.getComputedStyle(this.thumbsContainer);
    const gap = parseFloat(style.gap || "0");
    const itemWidth = firstThumb.getBoundingClientRect().width + gap;

    let scrollPosition = 0;

    if (this.currentIndex >= this.maxVisibleThumbs) {
      const offsetIndex = this.currentIndex - (this.maxVisibleThumbs - 1);
      scrollPosition = offsetIndex * itemWidth;
    }

    this.thumbsContainer.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });
  }
}

PluginRegistry.register("gallery-manager", GalleryManager);
